import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Download, Smartphone, RefreshCw } from 'lucide-react';

// Aktuelle App-Version - bei jedem Deploy erhöhen!
export const APP_VERSION = '2026.01.20.1';

const PWAInstall = ({ currentUser, justRegistered = false }) => {
  const { t } = useTranslation();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [showUpdatePrompt, setShowUpdatePrompt] = useState(false);
  const [showManualInstructions, setShowManualInstructions] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);
  const [isInStandaloneMode, setIsInStandaloneMode] = useState(false);
  const [promptCount, setPromptCount] = useState(0);

  // Nutzungszähler und Prompt-Logik
  const PROMPT_AT_USAGE = [1, 3, 9, 16]; // Nach welchen Nutzungen der Prompt erscheinen soll
  
  useEffect(() => {
    // Detect iOS and Android
    const ua = navigator.userAgent;
    const iOS = /iPad|iPhone|iPod/.test(ua);
    const android = /Android/.test(ua);
    setIsIOS(iOS);
    setIsAndroid(android);

    // Detect if already installed/standalone
    const standalone = window.matchMedia('(display-mode: standalone)').matches || 
                      window.navigator.standalone;
    setIsInStandaloneMode(standalone);

    // Listen for PWA install prompt
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // Zeige Prompt basierend auf Nutzungslogik
      checkAndShowPrompt();
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Nutzung zählen
    incrementUsageCount();
    
    // Prüfe auf Updates wenn im Standalone-Modus
    if (standalone) {
      checkForUpdates();
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  // Effekt für "gerade registriert" - sofort Prompt zeigen
  useEffect(() => {
    if (justRegistered && !isInStandaloneMode) {
      // Kleine Verzögerung damit die Registrierung abgeschlossen ist
      setTimeout(() => {
        setShowInstallPrompt(true);
        localStorage.setItem('pwaPromptCount', '1');
        setPromptCount(1);
      }, 2000);
    }
  }, [justRegistered, isInStandaloneMode]);

  const incrementUsageCount = () => {
    const count = parseInt(localStorage.getItem('appUsageCount') || '0') + 1;
    localStorage.setItem('appUsageCount', count.toString());
    checkAndShowPrompt(count);
  };

  const checkAndShowPrompt = (usageCount = null) => {
    // Nicht zeigen wenn bereits installiert
    if (isInStandaloneMode) return;
    
    // Nicht zeigen wenn User Prompts deaktiviert hat
    if (localStorage.getItem('pwaPromptsDisabled') === 'true') return;
    
    const count = usageCount || parseInt(localStorage.getItem('appUsageCount') || '0');
    const currentPromptCount = parseInt(localStorage.getItem('pwaPromptCount') || '0');
    
    // Prüfe ob wir bei einer der definierten Nutzungszahlen sind
    if (PROMPT_AT_USAGE.includes(count) && currentPromptCount < PROMPT_AT_USAGE.indexOf(count) + 1) {
      setShowInstallPrompt(true);
      localStorage.setItem('pwaPromptCount', (currentPromptCount + 1).toString());
      setPromptCount(currentPromptCount + 1);
    }
  };

  const checkForUpdates = async () => {
    try {
      // Gespeicherte Version prüfen
      const installedVersion = localStorage.getItem('pwaInstalledVersion');
      
      if (installedVersion && installedVersion !== APP_VERSION) {
        // Neue Version verfügbar!
        setShowUpdatePrompt(true);
      }
      
      // Aktuelle Version speichern
      localStorage.setItem('pwaInstalledVersion', APP_VERSION);
      
      // Service Worker Update prüfen
      if ('serviceWorker' in navigator) {
        const registration = await navigator.serviceWorker.ready;
        registration.update();
        
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              // Neuer Service Worker verfügbar
              setShowUpdatePrompt(true);
            }
          });
        });
      }
    } catch (error) {
      console.error('Update check failed:', error);
    }
  };

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      // Native install prompt verfügbar
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      
      if (outcome === 'accepted') {
        setDeferredPrompt(null);
        setShowInstallPrompt(false);
        // Version speichern für Update-Checks
        localStorage.setItem('pwaInstalledVersion', APP_VERSION);
      }
    } else {
      // Kein nativer Prompt - zeige manuelle Anleitung
      setShowManualInstructions(true);
    }
  };

  const handleDismiss = () => {
    setShowInstallPrompt(false);
  };

  const handleDisablePrompts = () => {
    localStorage.setItem('pwaPromptsDisabled', 'true');
    setShowInstallPrompt(false);
  };

  const handleUpdate = () => {
    // Cache leeren und Seite neu laden
    if ('caches' in window) {
      caches.keys().then(names => {
        names.forEach(name => caches.delete(name));
      });
    }
    // Service Worker überspringen wenn vorhanden
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({ type: 'SKIP_WAITING' });
    }
    // Seite hart neu laden
    window.location.reload(true);
  };

  // Update-Prompt anzeigen (hat Priorität)
  if (showUpdatePrompt) {
    return (
      <div className="fixed bottom-4 left-4 right-4 bg-blue-600 text-white p-4 rounded-lg shadow-lg z-40 max-w-sm mx-auto">
        <div className="flex items-center mb-3">
          <RefreshCw className="w-6 h-6 mr-2" />
          <h3 className="font-semibold">Update verfügbar</h3>
        </div>
        
        <p className="text-sm mb-3">
          Eine neue Version von Babelmix ist verfügbar. Bitte aktualisieren Sie für die beste Erfahrung.
        </p>
        
        <button
          onClick={handleUpdate}
          className="bg-white text-blue-600 px-4 py-2 rounded-md font-medium flex items-center justify-center w-full hover:bg-gray-100 transition-colors"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Jetzt aktualisieren
        </button>
      </div>
    );
  }

  // Nicht zeigen wenn bereits installiert
  if (isInStandaloneMode) {
    return null;
  }

  // Nicht zeigen wenn Prompts deaktiviert
  if (localStorage.getItem('pwaPromptsDisabled') === 'true') {
    return null;
  }

  if (!showInstallPrompt) {
    return null;
  }

  // Prüfe ob dies der letzte Prompt ist (16. Nutzung)
  const isLastPrompt = promptCount >= 4;

  return (
    <>
      <div className="fixed bottom-4 left-4 right-4 bg-emerald-600 text-white p-4 rounded-lg shadow-lg z-40 max-w-sm mx-auto">
        <button
          onClick={handleDismiss}
          className="absolute top-2 right-2 text-white hover:bg-emerald-700 rounded-full p-1"
          title={t('common.close')}
        >
          <X className="w-4 h-4" />
        </button>

        <div className="flex items-center mb-3">
          <Smartphone className="w-6 h-6 mr-2" />
          <h3 className="font-semibold">{t('pwa.installTitle')}</h3>
        </div>

        <p className="text-sm mb-3">
          {t('pwa.installDesc')}
        </p>

        {isIOS ? (
          <div className="text-xs">
            <p className="mb-2">{t('pwa.iosInstructions')}</p>
            <ol className="list-decimal list-inside space-y-1">
              <li>{t('pwa.ios1', 'Tippen Sie auf (⋯) unten rechts in Safari')}</li>
              <li>{t('pwa.ios2', 'Wählen Sie "Teilen" (□↑)')}</li>
              <li>{t('pwa.ios3', 'Tippen Sie auf "Zum Home-Bildschirm"')}</li>
            </ol>
          </div>
        ) : (
          <button
            onClick={handleInstallClick}
            className="bg-white text-emerald-600 px-4 py-2 rounded-md font-medium flex items-center justify-center w-full hover:bg-gray-100 transition-colors"
          >
            <Download className="w-4 h-4 mr-2" />
            {t('pwa.install')}
          </button>
        )}

        {/* Bei letztem Prompt: Option zum Deaktivieren */}
        {isLastPrompt && (
          <button
            onClick={handleDisablePrompts}
            className="mt-3 text-xs opacity-70 hover:opacity-100 underline w-full text-center"
          >
            {t('pwa.dontAskAgain')}
          </button>
        )}
      </div>

      {/* Modal für manuelle Android-Installation */}
      {showManualInstructions && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/60">
          <div className="bg-white rounded-xl shadow-2xl max-w-sm w-full p-5">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                <Smartphone className="w-5 h-5 text-emerald-600" />
                {t('pwa.installTitle', 'App installieren')}
              </h3>
              <button 
                onClick={() => setShowManualInstructions(false)}
                className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
              >
                ×
              </button>
            </div>

            {isAndroid && (
              <div className="space-y-3">
                <p className="text-sm text-gray-600">
                  {t('pwa.androidInstructions', 'So installieren Sie Babelmix auf Ihrem Android-Gerät:')}
                </p>
                <ol className="text-sm text-gray-700 space-y-2">
                  <li className="flex items-start gap-2">
                    <span className="bg-emerald-100 text-emerald-700 rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold flex-shrink-0">1</span>
                    <span>{t('pwa.android1', 'Tippen Sie auf die drei Punkte (⋮) oben rechts im Browser')}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="bg-emerald-100 text-emerald-700 rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold flex-shrink-0">2</span>
                    <span>{t('pwa.android2', 'Wählen Sie "Zum Startbildschirm hinzufügen" oder "App installieren"')}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="bg-emerald-100 text-emerald-700 rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold flex-shrink-0">3</span>
                    <span>{t('pwa.android3', 'Bestätigen Sie die Installation')}</span>
                  </li>
                </ol>
                <div className="mt-4 p-3 bg-emerald-50 rounded-lg text-sm text-emerald-700 flex items-start gap-2">
                  <span>💡</span>
                  <span>{t('pwa.androidTip', 'Nach der Installation finden Sie die App auf Ihrem Startbildschirm.')}</span>
                </div>
              </div>
            )}

            {!isAndroid && !isIOS && (
              <div className="space-y-3">
                <p className="text-sm text-gray-600">
                  Installieren Sie die App über das Browser-Menü oder die Adressleiste.
                </p>
                <p className="text-sm text-gray-500">
                  Suchen Sie nach "Installieren" oder "Zum Startbildschirm hinzufügen".
                </p>
              </div>
            )}

            <button 
              onClick={() => setShowManualInstructions(false)}
              className="w-full mt-4 bg-emerald-600 hover:bg-emerald-700 text-white py-2 px-4 rounded-lg font-medium transition-colors"
            >
              {t('common.close', 'Verstanden')}
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default PWAInstall;
